/**
 * Склеить стенд с его двойником.
 *
 * Двойники появлялись, когда визитку снимали при втором подходе и приложение
 * заводило новый стенд. Камни разъезжались по двум карточкам. Здесь человек
 * выбирает, с каким стендом этот — одно и то же, и всё содержимое переезжает.
 *
 * Похожие по названию стоят сверху: искать двойника в списке из сорока
 * стендов у стенда выставки никто не станет.
 */
import { useEffect, useState } from 'react';
import { db, type LocalSupplier } from '../db';
import { apiFetch, syncNow } from '../sync';
import { plural } from '@core';
import { nameKey } from '../lib/twins';
import { whereIs } from '../lib/where';

export function MergeSheet({ supplier, onClose, onMerged }: {
  supplier: LocalSupplier;
  onClose: () => void;
  /** Куда влили — туда и переходим. */
  onMerged: (targetId: string) => void;
}) {
  const [list, setList] = useState<Array<{ s: LocalSupplier; stones: number; twin: boolean }>>([]);
  const [busy, setBusy] = useState('');
  const [problem, setProblem] = useState('');

  useEffect(() => {
    void (async () => {
      const [all, stones] = await Promise.all([db.suppliers.toArray(), db.stones.toArray()]);
      const count = new Map<string, number>();
      for (const st of stones) count.set(st.supplierId, (count.get(st.supplierId) ?? 0) + 1);
      const mine = nameKey(supplier.companyName).replace(/\s/g, '');
      const rows = all
        .filter((s) => s.id !== supplier.id && s.exhibitionId === supplier.exhibitionId)
        .map((s) => ({
          s,
          stones: count.get(s.id) ?? 0,
          twin: Boolean(mine) && nameKey(s.companyName).replace(/\s/g, '') === mine,
        }))
        .sort((a, b) => Number(b.twin) - Number(a.twin) || a.s.companyName.localeCompare(b.s.companyName));
      setList(rows);
    })();
  }, [supplier.id]);

  async function merge(target: LocalSupplier) {
    if (!navigator.onLine) {
      setProblem('Склейка делается на сервере — нужна связь. Снятое при этом никуда не денется.');
      return;
    }
    const ok = window.confirm(
      `Склеить «${supplier.companyName}» со стендом «${target.companyName}»?\n\n`
      + `Камни, контакты и визитки перейдут в «${target.companyName}». Номера камней не изменятся. `
      + 'Пустой двойник уйдёт в корзину.');
    if (!ok) return;

    setBusy(target.id);
    setProblem('');
    try {
      // Сначала отправляем всё, что лежит в очереди: иначе камень, снятый
      // только что, догнал бы уже влитый стенд.
      await syncNow();
      const res = await apiFetch(`/api/suppliers/${supplier.id}/merge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ into: target.id }),
      });
      const d = await res.json().catch(() => ({} as any));
      if (!res.ok) { setProblem(d?.error ?? 'Не получилось склеить.'); return; }

      // На телефоне переносим сразу, не дожидаясь синхронизации: человек
      // должен увидеть камни на месте, как только закроется окно.
      await db.stones.where('supplierId').equals(supplier.id).modify({ supplierId: target.id });
      await db.media.where('subjectId').equals(supplier.id).modify({ subjectId: target.id });
      const t = await db.suppliers.get(target.id);
      if (t) {
        const have = new Set((t.contacts ?? []).map((c) => c.value.replace(/\D/g, '').slice(-9) || c.value));
        const extra = (supplier.contacts ?? []).filter(
          (c) => !have.has(c.value.replace(/\D/g, '').slice(-9) || c.value));
        await db.suppliers.update(target.id, { contacts: [...(t.contacts ?? []), ...extra] });
      }
      await db.suppliers.delete(supplier.id);
      void syncNow();
      onMerged(target.id);
    } catch (e: any) {
      setProblem(`Не получилось: ${e?.message ?? e}`);
    } finally {
      setBusy('');
    }
  }

  return (
    <>
      <div className="backdrop" onClick={onClose} />
      <div className="sheet">
        <div className="grip" />
        <h2 className="t">Это тот же стенд, что…</h2>
        <p className="lead">
          Выберите, с каким стендом склеить «{supplier.companyName}». Всё переедет туда,
          номера камней сохранятся.
        </p>

        {problem && <div className="memo warn">{problem}</div>}

        {list.length === 0 ? (
          <div className="blankslate">Других стендов на этой выставке нет.</div>
        ) : (
          <div className="list">
            {list.map(({ s, stones, twin }) => (
              <button key={s.id} className="row" disabled={Boolean(busy)} onClick={() => void merge(s)}>
                <span className="grow">
                  <span className="nm">{s.companyName}</span>
                  <span className="sub">
                    {[twin && 'похоже на двойника', whereIs(s),
                      `${stones} ${plural(stones, 'камень', 'камня', 'камней')}`].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <span className="chev">{busy === s.id ? '…' : '›'}</span>
              </button>
            ))}
          </div>
        )}

        <div className="dock" style={{ position: 'static', padding: '12px 0 0' }}>
          <button className="btn btn-secondary" onClick={onClose}>Отмена</button>
        </div>
      </div>
    </>
  );
}
